// src/templates/desktop/DashboardDesktop.jsx
import React from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useDashboardManutencao } from "../../hooks/useDashboardManutencao";

function fmtInt(n) {
  const v = Number(n) || 0;
  return v.toLocaleString("pt-BR");
}

function fmtHours(n, casas = 1) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  return v.toFixed(casas).replace(".", ",");
}

function fmtPct(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "-";
  const sign = v > 0 ? "+" : "";
  return `${sign}${v.toFixed(1).replace(".", ",")}%`;
}

function KpiTile({ title, value, hint, accent, badge }) {
  const letter = (badge || String(title || "").slice(0, 1)).toUpperCase();
  return (
    <div className="pc-card pc-card-ab" style={{ borderLeft: `6px solid ${accent}` }}>
      <div className="pc-card-ab-badge" style={{ background: accent }}>{letter}</div>
      <div className="pc-card-ab-main">
        <div className="pc-card-ab-value" style={{ fontSize: 22, fontWeight: 900 }}>{value}</div>
        <div className="pc-card-ab-label" style={{ fontSize: 12, opacity: 0.8 }}>{title}</div>
        {hint ? <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>{hint}</div> : null}
      </div>
      <div className="pc-card-ab-chart" style={{ background: accent, opacity: 0.25 }} />
    </div>
  );
}

function TopOsCard({ item }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 12,
        padding: 12,
        borderRadius: 12,
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(148,163,184,0.18)",
      }}
    >
      <div
        style={{
          width: 40,
          height: 40,
          borderRadius: 12,
          display: "grid",
          placeItems: "center",
          background: "rgba(59,130,246,0.18)",
          border: "1px solid rgba(59,130,246,0.35)",
          flex: "0 0 auto",
          fontWeight: 800,
        }}
      >
        OS
      </div>
      <div style={{ minWidth: 0, flex: 1 }}>
        <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}>
          <div style={{ fontWeight: 800 }}>{item.maquina}</div>
          <div style={{ opacity: 0.75, fontSize: 12 }}>({item.codigo})</div>
        </div>
        <div
          style={{
            opacity: 0.85,
            fontSize: 13,
            marginTop: 2,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
          title={item.titulo}
        >
          {item.titulo}
        </div>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 8, fontSize: 12, opacity: 0.85 }}>
          <span>Status: <strong style={{ opacity: 1 }}>{item.status}</strong></span>
          <span>Crit.: <strong style={{ opacity: 1 }}>{item.criticidade}</strong></span>
        </div>
      </div>

      <div style={{ textAlign: "right", flex: "0 0 auto" }}>
        <div style={{ fontSize: 12, opacity: 0.8 }}>Parada (aprox.)</div>
        <div style={{ fontWeight: 900, fontSize: 18 }}>{fmtHours(item.horas, 1)} h</div>
      </div>
    </div>
  );
}

export default function DashboardDesktop() {
  const { usuario } = useAuth();
  const { loading, error, kpis, deltas, topOsAbertas } = useDashboardManutencao({ grupoId: usuario?.grupoId });

  return (
    <>
      {/* TOP KPIs (estilo Produção) */}
      <section
        className="pc-row"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(4, minmax(0, 1fr))",
          gap: 12,
        }}
      >
        <KpiTile
          title="OS em aberto"
          value={loading ? "..." : fmtInt(kpis.osAbertasAgora)}
          hint={loading ? "" : `${fmtPct(deltas.osAbertasAgora)} vs mês anterior`}
          accent="#38bdf8"
          badge="OS"
        />
        <KpiTile
          title="Máquinas paradas"
          value={loading ? "..." : fmtInt(kpis.maquinasParadasAgora)}
          hint={loading ? "" : `${fmtPct(deltas.maquinasParadasAgora)} vs mês anterior`}
          accent="#ef4444"
          badge="MP"
        />
        <KpiTile
          title="Aguardando material"
          value={loading ? "..." : fmtInt(kpis.aguardandoMaterialAgora)}
          hint={loading ? "" : `${fmtPct(deltas.aguardandoMaterialAgora)} vs mês anterior`}
          accent="#f59e0b"
          badge="AM"
        />
        <KpiTile
          title="Paradas no mês"
          value={loading ? "..." : `${fmtHours(kpis.paradasHorasMes, 1)} h`}
          hint={loading ? "" : `${fmtPct(deltas.paradasHorasMes)} vs mês anterior`}
          accent="#a855f7"
          badge="H"
        />
      </section>

      {/* Linha A/B */}
      <section
        className="pc-row"
        style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 12, marginTop: 12 }}
      >
        <div className="pc-card" style={{ padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div className="pc-card-ab-badge">A</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, opacity: 0.85 }}>OS abertas no mês</div>
              <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{loading ? "..." : fmtInt(kpis.osAbertasMes)}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{loading ? "" : `${fmtPct(deltas.osAbertasMes)} vs mês anterior`}</div>
            </div>
          </div>
          <div className="pc-card-ab-chart pc-card-ab-chart-a" />
        </div>

        <div className="pc-card" style={{ padding: 14, display: "flex", alignItems: "center", justifyContent: "space-between", gap: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
            <div className="pc-card-ab-badge">B</div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 12, opacity: 0.85 }}>OS finalizadas no mês</div>
              <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{loading ? "..." : fmtInt(kpis.osFinalizadasMes)}</div>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>{loading ? "" : `${fmtPct(deltas.osFinalizadasMes)} vs mês anterior`}</div>
            </div>
          </div>
          <div className="pc-card-ab-chart pc-card-ab-chart-b" />
        </div>
      </section>

      {/* OS críticas + indicadores rápidos */}
      <section
        className="pc-row"
        style={{ display: "grid", gridTemplateColumns: "minmax(0, 2fr) minmax(0, 1fr)", gap: 12, marginTop: 12 }}
      >
        <div className="pc-card" style={{ padding: 14, minWidth: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10 }}>
            <div>
              <h3 style={{ margin: 0 }}>Paradas críticas (OS) — Top 3 agora</h3>
              <div style={{ fontSize: 12, opacity: 0.8, marginTop: 4 }}>Ordenado por maior tempo de parada (aproximação).</div>
            </div>
            <span className="pc-card-chart-tag">Em aberto</span>
          </div>

          {error ? (
            <div className="pc-alert pc-alert-error" style={{ marginTop: 12 }}>{error}</div>
          ) : loading ? (
            <div style={{ marginTop: 12, opacity: 0.85 }}>Carregando...</div>
          ) : topOsAbertas.length ? (
            <div style={{ display: "grid", gap: 10, marginTop: 12 }}>
              {topOsAbertas.map((os) => (
                <TopOsCard key={os.id} item={os} />
              ))}
            </div>
          ) : (
            <div style={{ marginTop: 12, opacity: 0.8 }}>Sem OS com máquina parada em aberto.</div>
          )}
        </div>

        <div className="pc-card" style={{ padding: 14, minWidth: 0 }}>
          <h3 style={{ marginTop: 0 }}>Indicadores rápidos (mês)</h3>
          <div className="pc-side-item">
            <span>MTBF (mês)</span>
            <strong>{loading ? "..." : (kpis.mtbfHorasMes == null ? "-" : `${fmtHours(kpis.mtbfHorasMes, 1)} h`)}</strong>
          </div>
          <div className="pc-side-item">
            <span>MTTR (mês)</span>
            <strong>{loading ? "..." : (kpis.mttrHorasMes == null ? "-" : `${fmtHours(kpis.mttrHorasMes, 1)} h`)}</strong>
          </div>
          <div className="pc-side-item">
            <span>Maior parada (evento)</span>
            <strong>{loading ? "..." : `${fmtHours(kpis.maiorParadaHorasMes, 1)} h`}</strong>
          </div>
          <div className="pc-side-item">
            <span>Máquina mais crítica</span>
            <strong title={kpis.maquinaMaisCriticaMes}>{loading ? "..." : kpis.maquinaMaisCriticaMes}</strong>
          </div>
        </div>
      </section>
    </>
  );
}
